import { useEffect, useState } from "react";
import { CheckCircle, XCircle, DownloadSimple, Plus, Trash } from "@phosphor-icons/react";
import api, { apiError, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const SIGNUP_BADGE = { pending: "bg-[#F5A623]/15 text-[#F5A623]", approved: "bg-[#A9C97E]/15 text-[#A9C97E]", rejected: "bg-[#F87171]/15 text-[#F87171]" };
const ACCT_BADGE = { trial: "bg-[#9D5CFF]/15 text-[#C9A8FF]", active: "bg-[#A9C97E]/15 text-[#A9C97E]", suspended: "bg-[#F87171]/15 text-[#F87171]" };
const BILL_BADGE = { paid: "bg-[#A9C97E]/15 text-[#A9C97E]", unpaid: "bg-[#F5A623]/15 text-[#F5A623]", overdue: "bg-[#F87171]/15 text-[#F87171]" };

function Signups() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get(`/admin/signups?status=${filter}`).then((r) => setRows(r.data)).catch(() => toast.error("Failed to load applications"));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (sid, action) => {
    try {
      await api.post(`/admin/signups/${sid}/${action}`);
      toast.success(action === "approve" ? "Approved — venue + owner account created, activation email sent" : "Application rejected");
      load();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    }
  };
  return (
    <div className="space-y-4" data-testid="admin-signups">
      <div className="flex gap-2">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`admin-signups-filter-${s}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${filter === s ? "bg-primary text-white" : "bg-accent text-muted-foreground"}`}>{s}</button>
        ))}
      </div>
      <Card className="border-border shadow-none">
        <CardContent className="p-0">
          {(rows || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-0 flex-wrap" data-testid={`admin-signup-${r.id}`}>
              <div>
                <p className="font-medium text-sm">{r.venue_name} <span className="text-xs text-muted-foreground">· {r.restaurant_type || "venue"}</span></p>
                <p className="text-xs text-muted-foreground">{r.owner_name} · {r.email} · {r.phone || "no phone"} · applied {r.created_at?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={SIGNUP_BADGE[r.status]} data-testid={`admin-signup-status-${r.id}`}>{r.status}</Badge>
                {r.status === "pending" && (
                  <>
                    <Button size="sm" className="h-7 text-xs bg-[#A9C97E] hover:bg-[#8FA98C] text-black" onClick={() => act(r.id, "approve")} data-testid={`admin-approve-${r.id}`}>
                      <CheckCircle size={13} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => act(r.id, "reject")} data-testid={`admin-reject-${r.id}`}>
                      <XCircle size={13} className="mr-1" /> Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
          {rows && rows.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="admin-signups-empty">No {filter} applications.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function Venues() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/venues").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load venues"));
  useEffect(() => { load(); }, []);
  const setAcct = async (vid, account) => {
    try {
      await api.put(`/admin/venues/${vid}/account`, account);
      toast.success("Account updated");
      load();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    }
  };
  return (
    <div className="space-y-3" data-testid="admin-venues">
      {(rows || []).map((v) => {
        const a = v.account || {};
        return (
          <Card key={v.id} className="border-border shadow-none" data-testid={`admin-venue-${v.id}`}>
            <CardContent className="py-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium">{v.name}</p>
                <p className="text-xs text-muted-foreground">
                  {v.owner ? `${v.owner.first_name} ${v.owner.last_name} · ${v.owner.email}` : "no owner"} · {v.staff_count} active staff
                </p>
              </div>
              <Badge className={ACCT_BADGE[a.status] || ""} data-testid={`admin-venue-status-${v.id}`}>{a.status || "—"}</Badge>
              <Badge className={BILL_BADGE[a.billing_status] || ""} data-testid={`admin-venue-billing-${v.id}`}>{a.billing_status || "—"}</Badge>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={a.status || "trial"} onValueChange={(val) => setAcct(v.id, { ...a, status: val })}>
                  <SelectTrigger className="h-8 w-28 text-xs" data-testid={`admin-acct-status-${v.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["trial", "active", "suspended"].map((s) => <SelectItem key={s} value={s} data-testid={`admin-acct-status-opt-${v.id}-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={a.plan || "starter"} onValueChange={(val) => setAcct(v.id, { ...a, plan: val })}>
                  <SelectTrigger className="h-8 w-28 text-xs" data-testid={`admin-acct-plan-${v.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["starter", "growth", "enterprise"].map((s) => <SelectItem key={s} value={s} data-testid={`admin-acct-plan-opt-${v.id}-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={a.billing_cycle || "monthly"} onValueChange={(val) => setAcct(v.id, { ...a, billing_cycle: val })}>
                  <SelectTrigger className="h-8 w-24 text-xs" data-testid={`admin-acct-cycle-${v.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["monthly", "annual"].map((s) => <SelectItem key={s} value={s} data-testid={`admin-acct-cycle-opt-${v.id}-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={a.billing_status || "unpaid"} onValueChange={(val) => setAcct(v.id, { ...a, billing_status: val })}>
                  <SelectTrigger className="h-8 w-24 text-xs" data-testid={`admin-acct-billing-${v.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["unpaid", "paid", "overdue"].map((s) => <SelectItem key={s} value={s} data-testid={`admin-acct-billing-opt-${v.id}-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <a href={`${API_BASE}/admin/venues/${v.id}/export`} data-testid={`admin-export-${v.id}`}>
                  <Button size="sm" variant="outline" className="h-8 text-xs"><DownloadSimple size={14} className="mr-1" /> Export data</Button>
                </a>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function SiteContent() {
  const [tiers, setTiers] = useState(null);
  const [points, setPoints] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/site").then((r) => { setTiers(r.data.pricing_tiers); setPoints(r.data.marketing_points); }).catch(() => toast.error("Failed to load site content"));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site", { pricing_tiers: tiers, marketing_points: points });
      toast.success("Marketing site content saved — live immediately");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };
  if (!tiers) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="admin-site">
      <Card className="border-border shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Pricing tiers (shown on the public site)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {tiers.map((t, i) => (
            <div key={i} className="border border-border rounded-lg p-4 space-y-3" data-testid={`admin-tier-${i}`}>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Tier name</Label>
                  <Input value={t.name} onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} data-testid={`admin-tier-name-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">Price label</Label>
                  <Input value={t.price_label} onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, price_label: e.target.value } : x))} data-testid={`admin-tier-price-${i}`} placeholder="e.g. R 1 450 /month" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Blurb</Label>
                <Input value={t.blurb} onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, blurb: e.target.value } : x))} data-testid={`admin-tier-blurb-${i}`} /></div>
              <div className="space-y-1"><Label className="text-xs">Features (one per line)</Label>
                <textarea rows={4} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  value={(t.features || []).join("\n")}
                  onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, features: e.target.value.split("\n") } : x))}
                  data-testid={`admin-tier-features-${i}`} /></div>
              <div className="flex justify-end">
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setTiers(tiers.filter((_, j) => j !== i))} data-testid={`admin-tier-del-${i}`}>
                  <Trash size={13} className="mr-1" /> Remove tier</Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setTiers([...tiers, { name: "", price_label: "", blurb: "", features: [] }])} data-testid="admin-tier-add">
            <Plus size={14} className="mr-1" /> Add tier
          </Button>
        </CardContent>
      </Card>
      <Card className="border-border shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Capability highlights (public site)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <textarea rows={8} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            value={points.join("\n")} onChange={(e) => setPoints(e.target.value.split("\n"))} data-testid="admin-points" />
          <Button onClick={save} disabled={busy} className="bg-primary hover:bg-[#8B5CF6] text-white" data-testid="admin-site-save">
            {busy ? "Saving…" : "Save site content"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("signups");
  if (!user?.is_platform_admin) return <p className="text-muted-foreground" data-testid="admin-no-access">Platform admin only.</p>;
  const TABS = [["signups", "Sign-up queue"], ["venues", "Venue accounts"], ["site", "Marketing site"]];
  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Platform console</h1>
        <p className="text-muted-foreground mt-1">Applications, venue accounts and public site content.</p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "signups" && <Signups />}
      {tab === "venues" && <Venues />}
      {tab === "site" && <SiteContent />}
    </div>
  );
}
